import * as React from 'react';
import { connectToItemCache } from '../../decorators/connectToItemCache';
import { IItemCache } from '../../../models/IItemCacheObserver';
import { displayNameFacet }
  from '../../../facets/ItemFacets';
import { INameFieldProps, NameField } from '../name/NameField';
import { getNewSignal, getStorageSignals, getLifeCycleSignals } from '../signal/SignalIcon';
import { DotDotDotField } from '../dotdotdot/DotDotDotField';
import { ShareHeroField } from '../shareHero/ShareHeroField';
import { FollowedHeroField } from '../followedHero/FollowedHeroField';
import { SignalField } from '@uifabric/experiments/lib/components/signals/SignalField';
import * as HeroStyles from '../dotdotdot/DotDotDotField.scss';
import { WithActionSet, IWithActionSetStateProps, evaluateAction, INITIAL_ACTION_STATE } from '../../withAction/WithAction';
import { ItemShareAction } from '../../actions/share/ShareAction';
import { ItemFollowAction } from '../../actions/follow/FollowAction';
import { IItemHoverCardProps } from '../../../controls/itemHoverCardControl';
import CommandStrings from '../../../components/commands/Commands.resx';
import { mobileOpenControl } from '../../../controls/mobileOpenControl';
import { fabricOptimizerControl, IFabricOptimizerOverrides } from '../../../controls/fabricOptimizerControl';
import { getItemHoverCard } from '../../fileHoverCard/getItemHoverCard';
import { DeferredRender, IDeferredRenderStateProps } from '../../../utilities/components/DeferredRender';
import { getFieldRenderBarrier } from '../FieldHelper';

interface ISignalNameFieldProp {
  beforeSignals: React.ReactNode[] | React.ReactNode;
  afterSignals: React.ReactNode[] | React.ReactNode;
  signalTitles?: string[];
  hoverCard: React.ComponentType<IItemHoverCardProps> | undefined;
  shareAction?: JSX.Element | null;
  followAction?: JSX.Element | null;
  children?: React.ReactNode;
  pageLoadBarrier?: Promise<void>;
  renderShareHero?: IFabricOptimizerOverrides['renderShareHero'];
  renderMenuHero?: IFabricOptimizerOverrides['renderMenuHero'];
}

interface IHeroActions {
  share: JSX.Element | null;
  follow: JSX.Element | null;
}

const LINE_BREAK = '\u000D';
const strings = CommandStrings;

function onContextMenu(ev: React.MouseEvent<HTMLButtonElement>): void {
  const menuEvent = document.createEvent('CustomEvent');
  menuEvent.initCustomEvent('contextmenu', true /*canBubble*/, true /*cancelable*/, ev);
  ev.currentTarget.dispatchEvent(menuEvent);
}

export const NameHeroField: React.ComponentType<{
  itemKey: string;
} & Pick<INameFieldProps, 'isDisabled' | 'onClick'>> = connectToItemCache((itemCache: IItemCache, props: {
  itemKey: string;
}): Pick<INameFieldProps, 'linkText' | 'mobileOpenProperty'> & ISignalNameFieldProp => {
  const {
    itemKey,
  } = props;

  const displayName = itemCache.demandItemFacet(displayNameFacet, itemKey);

  // Get signal icons
  const newSignalData = getNewSignal(itemCache, itemKey);
  const beforeSignals = newSignalData ? [newSignalData.signal] : undefined;
  const afterSignalsData = getAfterSignals(itemCache, itemKey);
  const signalTitles = afterSignalsData.titles;
  let afterSignals: React.ReactNode[] | undefined = afterSignalsData.signals;
  if (afterSignals && afterSignals.length === 0) {
    afterSignals = undefined;
  }

  const keyProps = {
    itemKey
  };

  const mobileOpenControlHandler = itemCache.demandItemFacet(mobileOpenControl, itemKey);

  const {
    mobileOpenProperty
  } = mobileOpenControl.evaluate(mobileOpenControlHandler)(itemCache, keyProps);

  const fabricOptimizerControlHandler = itemCache.demandItemFacet(fabricOptimizerControl, itemKey);
  // The FabricOptimizerControl is explicitly not composable
  const {
    renderMenuHero,
    renderShareHero,
    replaceSignals
  }: IFabricOptimizerOverrides = fabricOptimizerControlHandler && fabricOptimizerControlHandler(itemCache, {}) || {};

  const pageLoadBarrier = getFieldRenderBarrier(itemCache);

  const shareAction = evaluateAction(itemCache, React.createElement(ItemShareAction, keyProps));

  const followAction = evaluateAction(itemCache, React.createElement(ItemFollowAction, keyProps));

  return {
    beforeSignals: replaceSignals ? replaceSignals(beforeSignals) : beforeSignals,
    afterSignals: replaceSignals ? replaceSignals(afterSignals) : afterSignals,
    signalTitles: signalTitles,
    linkText: displayName,
    hoverCard: getItemHoverCard(itemCache, keyProps),
    mobileOpenProperty: mobileOpenProperty,
    shareAction,
    followAction,
    pageLoadBarrier,
    renderMenuHero,
    renderShareHero
  };
})(NameHeroFieldBase);

function NameHeroFieldBase(
  props: INameFieldProps & ISignalNameFieldProp & {
    itemKey: string;
  }
): JSX.Element {
  const {
    itemKey,
    beforeSignals,
    afterSignals,
    signalTitles,
    hoverCard: HoverCard,
    followAction,
    shareAction,
    pageLoadBarrier,
    renderMenuHero,
    renderShareHero,
    linkText,
    onClick
  } = props;

  const nameFieldTitle = (signalTitles &&
    (linkText + (linkText ? LINE_BREAK : '') + signalTitles.join(LINE_BREAK))) || '';

  const actions: IHeroActions = {
    share: shareAction as JSX.Element,
    follow: followAction as JSX.Element
  };

  const menuHeroField = (
    <DotDotDotField
      onClick={ onContextMenu }
      ariaLabel={ strings.ShowActions }
      title={ strings.ShowActions }
      pageLoadBarrier={ pageLoadBarrier }
    />
  );

  const content: JSX.Element = (
    <div className={ `${HeroStyles.fieldRendererRendererWithCommands}` }>
      <div className={ `${HeroStyles.nameFieldWidthOnHover}` }>
        <SignalField
          signalsFieldMode='wide'
          before={
            beforeSignals ? (
              <DeferredRender barrier={ pageLoadBarrier }>
                {
                  beforeSignals
                }
              </DeferredRender>
            ) : null
          }
          after={
            afterSignals ? (
              <DeferredRender barrier={ pageLoadBarrier }>
                {
                  <span className={ `${HeroStyles.signalsShowHide}` }>
                    { afterSignals }
                  </span>
                }
              </DeferredRender>
            ) : null
          }
        >
          <DeferredRender
            barrier={ pageLoadBarrier }
          >
            { ({ hasResolved }: IDeferredRenderStateProps) => {
              return (
                <NameField
                  ariaDescription={ HoverCard ? strings.FileHoverCardShortcutHint : undefined }
                  isDisabled={ props.isDisabled }
                  linkText={ linkText }
                  onClick={ hasResolved ? onClick : undefined }
                  mobileOpenProperty={ props.mobileOpenProperty }
                  title={ nameFieldTitle }
                  disableTooltip={ !!HoverCard }
                />
              );
            } }
          </DeferredRender>
        </SignalField>
        { props.children || null }
      </div>
      <div className={ `${HeroStyles.fieldRendererHeroCommandContainer}` }>
        <WithActionSet<keyof IHeroActions> actions={ actions } >
          { ({ actions: actionStates }: IWithActionSetStateProps<keyof IHeroActions>) => {
            const {
              share: {
                isAvailable: isShareAvailable,
                execute: executeShare
              } = INITIAL_ACTION_STATE,
              follow: {
                isAvailable: isFollowAvailable,
                isToggled: hasFollowed = false,
                execute: executeFollow
              } = INITIAL_ACTION_STATE
            } = actionStates;

            const onClickShare = isShareAvailable && executeShare ? (event: React.MouseEvent<HTMLButtonElement>) => {
              event.persist();
              executeShare({ event }).catch(() => {
                // Ignore errors.
              });
            } : undefined;

            const onClickFollow = isFollowAvailable && executeFollow ? (event: React.MouseEvent<HTMLButtonElement>) => {
              event.persist();
              executeFollow({ event }).catch(() => {
                // Ignore errors.
              });
            } : undefined;

            return (
              <>
                { onClickShare ? (renderShareHero ? renderShareHero(onClickShare) :
                  <ShareHeroField
                    onClick={ onClickShare }
                    ariaLabel={ strings.ShareCommandTooltipV3 }
                    title={ strings.ShareCommandTooltipV3 }
                    pageLoadBarrier={ pageLoadBarrier }
                  />) : null }
                { onClickFollow ? (
                  <FollowedHeroField
                    onClick={ onClickFollow }
                    ariaLabel={ strings.FollowCommandTooltip }
                    title={ strings.FollowCommandTooltip }
                    pageLoadBarrier={ pageLoadBarrier }
                    hasFollowed={ hasFollowed }
                  />
                ) : null }
                { renderMenuHero ? renderMenuHero() : menuHeroField }
              </>
            );
          } }
        </WithActionSet>
      </div>
    </div>
  );

  return HoverCard ? (
    <HoverCard itemKey={ itemKey }>
      { content }
    </HoverCard>
  ) : content;
}

function getAfterSignals(itemCache: IItemCache, itemKey: string): {
  signals: React.ReactNode[],
  titles: string[]
} {
  const storageSignals = getStorageSignals(itemCache, itemKey);
  const hasStorage = storageSignals.length > 0;

  const maxLifeCycleCount = hasStorage ? 2 : 3;
  const lifeCycleSignals = getLifeCycleSignals(itemCache, itemKey, maxLifeCycleCount, false/*includeNew*/);

  const signals: React.ReactNode[] = [];
  const titles: string[] = [];

  if (hasStorage) {
    const storageSignal = storageSignals[0];
    signals.push(storageSignal.signal);
    titles.push(storageSignal.title || '');
  }

  for (const lifeCycleSignal of lifeCycleSignals) {
    signals.push(lifeCycleSignal.signal);
    titles.push(lifeCycleSignal.title || '');
  }

  return {
    signals: signals,
    titles: titles
  };
}
